/** tm/components/GovFeedDocViewer.tsx — 검토 패널 가운데 「원문 뷰어」 (2026-09-30 추가 확정 3)
 *
 *  · 탭: 회차별 파일(공고문 PDF · 본문 HWP 글 · 상세 요약 · 이미지). PDF 는 pdf.js(캔버스 + 글 층), 글은 문단 그대로.
 *  · PDF·글 모두: 검색창(형광 · n/N · ⏎/⇧⏎) · 근거 인용 형광(focus) — 지금 탭 → PDF → 본문 → 상세 요약 순으로 찾는다.
 *  · 글을 드래그해 고르면 작은 버튼 「사업명으로」「금액으로」「수행기관으로」「마감일로」… → onPick.
 *  인증: PDF 바이트·글은 헤더 인증 fetch, img·「새 창」 링크는 «원문 보기 전용» 토큰(?t=) (개발 서버의 img 는 blob).
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { C, T, R } from './ui';
import { AMBER } from './GovFeedParts';
import GovFeedPdfView, { loadPdfModel, scrollToCenter, type PdfMark } from './GovFeedPdfView';
import * as api from '../api';
import type { GovFeedDoc, GovFeedDocItem } from '../types';

export type PickField = 'name' | 'report_name' | 'amount' | 'agency' | 'deadline' | 'apply_start' | 'period';
export interface DocFocus { quote: string; label: string; nonce: number }

const USE_BLOB = process.env.NODE_ENV === 'development';
const docsCache = new Map<string, GovFeedDocItem[]>();
const textCache = new Map<string, string>();

// ── 글 찾기 ─────────────────────────────────────────────────────
const esc = (c: string) => c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** 공백·줄바꿈이 달라도 찾는다 (HWP 추출 글은 줄이 끊겨 있다) */
function flexRx(s: string, flags = 'g'): RegExp | null {
  const chars = Array.from(s.replace(/\s+/g, ''));
  if (!chars.length) return null;
  return new RegExp(chars.map(esc).join('\\s*'), flags);
}
function cleanQuote(q: string): string {
  return String(q || '').replace(/^[\s“”"'‘’「」…·.]+|[\s“”"'‘’「」…·.]+$/g, '').replace(/\.{3}|…/g, ' ').trim();
}
/** 인용문 위치 — 전체 → 앞 24자 → 뒤 24자 순으로 */
export function findQuote(text: string, quote: string): { start: number; end: number } | null {
  const q = cleanQuote(quote);
  if (!q || !text) return null;
  const parts = q.split(/\s*\/\s*/).filter(Boolean); // 근거가 「a / b」로 합쳐 온 경우 첫 조각
  for (const cand of [q, parts[0], q.replace(/\s+/g, '').slice(0, 24), q.replace(/\s+/g, '').slice(-24)]) {
    if (!cand || cand.replace(/\s+/g, '').length < 4) continue;
    const rx = flexRx(cand, '');
    const m = rx ? rx.exec(text) : null;
    if (m) return { start: m.index, end: m.index + m[0].length };
  }
  return null;
}

/** 고른 글의 날짜들 → 'YYYY-MM-DD' ('2026. 3. 11.(수) ~ 4. 15.' 의 뒷날짜는 앞 연도) */
export function parseDates(text: string): string[] {
  const out: string[] = [];
  const rx = /(20\d{2})\s*[.\-/년]\s*(\d{1,2})\s*[.\-/월]\s*(\d{1,2})|[~∼〜-]\s*(\d{1,2})\s*[.\-/월]\s*(\d{1,2})(?!\d)/g;
  let m: RegExpExecArray | null;
  let year = '';
  while ((m = rx.exec(text)) !== null) {
    const y = m[1] || year;
    const mo = Number(m[2] || m[4]); const d = Number(m[3] || m[5]);
    if (!y || mo < 1 || mo > 12 || d < 1 || d > 31) continue;
    year = y;
    out.push(`${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  }
  return out;
}

const kindLabel = (d: GovFeedDoc, list: GovFeedDoc[]) => {
  if (d.key === 'detail') return '상세 요약';
  const ext = (d.fileName.match(/\.([a-z0-9]+)$/i) || [])[1]?.toUpperCase();
  const base = d.converted ? `공고문 ${ext || 'HWP'}` : d.kind === 'pdf' ? '공고문 PDF' : d.kind === 'image' ? '이미지' : `본문${ext ? ` ${ext}` : ''}`;
  const same = list.filter(x => x.kind === d.kind && x.key !== 'detail');
  return same.length > 1 ? `${base} ${same.indexOf(d) + 1}` : base;
};

interface Props {
  groupId: string;
  focus: DocFocus | null;
  onPick: (field: PickField, text: string) => void;
  /** 없으면 「원문 접기」 단추를 숨긴다 (집중 모드) */
  onCollapse?: () => void;
  /** 값이 바뀌면 PDF 를 「폭 맞춤」으로 되돌린다 (집중 모드 켜고 끌 때) */
  fitKey?: string | number | boolean;
  /** 폭 조절 드래그 중 (지금은 캔버스라 쓰지 않지만 호환용) */
  dragging?: boolean;
}

const tabBtn = (act: boolean): React.CSSProperties => ({
  padding: '0 10px', height: 34, border: 'none', background: 'transparent', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
  borderBottom: `2px solid ${act ? C.accent : 'transparent'}`, marginBottom: -1,
  fontSize: T.sm, fontWeight: act ? 700 : 500, color: act ? C.text : C.sub,
});
const miniBtn: React.CSSProperties = { height: 26, padding: '0 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', fontSize: T.xs, color: C.sub, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 3, textDecoration: 'none', boxSizing: 'border-box' };

export default function GovFeedDocViewer({ groupId, focus, onPick, onCollapse, fitKey }: Props) {
  const [items, setItems] = useState<GovFeedDocItem[] | null>(() => docsCache.get(groupId) || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [itemIdx, setItemIdx] = useState(0);
  const [docKey, setDocKey] = useState<string>('');
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [src, setSrc] = useState<{ key: string; url: string } | null>(null);
  const [frameLoading, setFrameLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [hit, setHit] = useState(0);
  const [quoteRange, setQuoteRange] = useState<{ docId: string; start: number; end: number } | null>(null);
  const [quoteNonce, setQuoteNonce] = useState(0);
  const [missQuote, setMissQuote] = useState<string | null>(null);
  const [selTip, setSelTip] = useState<{ x: number; y: number; text: string } | null>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const markRef = useRef<HTMLElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pdfFail, setPdfFail] = useState<Set<string>>(new Set());
  const [dlErr, setDlErr] = useState<Record<string, string>>({}); // 원본을 받지 못함(빈 파일·웹 페이지) — 문서별 이유 (2026-10-03)
  const [pageInfo, setPageInfo] = useState<{ cur: number; total: number } | null>(null);
  const pageCb = useCallback((cur: number, total: number) => setPageInfo(p => (p && p.cur === cur && p.total === total ? p : { cur, total })), []);

  // ── 목록 ──
  useEffect(() => {
    let cancelled = false;
    const cached = docsCache.get(groupId);
    setItems(cached || null); setItemIdx(0); setDocKey(''); setQuoteRange(null); setMissQuote(null); setSearch(''); setError('');
    if (cached) return;
    setLoading(true);
    api.getGovFeedDocs(groupId)
      .then(r => { if (cancelled) return; const it = r.data?.items || []; docsCache.set(groupId, it); setItems(it); })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : '원문을 불러오지 못했습니다'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [groupId]);

  const item = items?.[itemIdx] || null;
  const docs = useMemo(() => item?.docs || [], [item]);
  const defaultKey = useMemo(() => (docs.find(d => d.kind === 'pdf') || docs.find(d => d.kind === 'text' && d.key !== 'detail') || docs[0])?.key || '', [docs]);
  const doc = docs.find(d => d.key === docKey) || docs.find(d => d.key === defaultKey) || null;
  const docId = item && doc ? `${item.itemId}/${doc.key}` : '';
  const textOf = useCallback((it: GovFeedDocItem, d: GovFeedDoc): string | null => {
    const id = `${it.itemId}/${d.key}`;
    const got = texts[id] ?? textCache.get(id);
    if (got != null) return got;
    // PDF 는 pdf.js 글 층 글을 쓴다(형광 위치가 맞게) — 서버가 뽑은 글(pypdf)은 pdf.js 가 실패했을 때만
    if (d.kind === 'pdf') return pdfFail.has(id) && typeof d.text === 'string' ? d.text : null; // (변환 PDF 실패 때 받아 온 글은 위 texts 에)
    return typeof d.text === 'string' ? d.text : null;
  }, [texts, pdfFail]);

  /** 비어 온 글(이전 회차)은 받아 온다 */
  const ensureText = useCallback(async (it: GovFeedDocItem, d: GovFeedDoc): Promise<string | null> => {
    const have = textOf(it, d);
    if (have != null || !d.viewUrl || d.kind === 'image') return have;
    try {
      const t = d.kind === 'pdf'
        ? await loadPdfModel(d.viewUrl).then(m => m.text).catch(async () => {
          setPdfFail(f => new Set(f).add(`${it.itemId}/${d.key}`));
          if (typeof d.text === 'string') return d.text;
          // HWP·Word 를 PDF 로 못 바꿨으면 서버가 뽑은 글로 (2026-10-01)
          return d.converted && d.viewUrl ? api.govFeedDocText(`${d.viewUrl}?as=text`).catch(() => null) : null;
        })
        : await api.govFeedDocText(d.viewUrl);
      if (t == null) return null;
      textCache.set(`${it.itemId}/${d.key}`, t);
      setTexts(m => ({ ...m, [`${it.itemId}/${d.key}`]: t }));
      return t;
    } catch { return null; }
  }, [textOf]);

  useEffect(() => { if (item && doc && doc.kind === 'text') void ensureText(item, doc); }, [item, doc, ensureText]);

  // ── PDF · 이미지 주소 ──
  useEffect(() => {
    if (!doc || !doc.viewUrl || doc.kind === 'text') { setSrc(null); return; }
    let cancelled = false;
    let blob = '';
    setFrameLoading(doc.kind === 'image');
    (USE_BLOB && doc.kind === 'image' ? api.govFeedDocBlob(doc.viewUrl).then(u => { blob = u; return u; }) : api.govFeedDocSrc(doc.viewUrl))
      .then(u => { if (!cancelled) setSrc({ key: docId, url: u }); else if (blob) URL.revokeObjectURL(blob); })
      .catch(e => { if (!cancelled) { setError(e instanceof Error ? e.message : '원문을 열 수 없습니다'); setFrameLoading(false); } });
    return () => { cancelled = true; if (blob) URL.revokeObjectURL(blob); };
  }, [doc, docId]);

  // ── 근거 인용 → 글 탭에서 찾아 형광 ──
  useEffect(() => {
    if (!focus || !items || !items.length) return;
    let cancelled = false;
    (async () => {
      const it = items[itemIdx] || items[0];
      const cands = [
        ...(doc && doc.kind !== 'image' ? [doc] : []),
        ...it.docs.filter(d => d.kind === 'pdf' && d !== doc),
        ...it.docs.filter(d => d.kind === 'text' && d.key !== 'detail' && d !== doc),
        ...it.docs.filter(d => d.key === 'detail' && d !== doc),
      ];
      for (const d of cands) {
        const t = await ensureText(it, d);
        if (cancelled) return;
        const r = t ? findQuote(t, focus.quote) : null;
        if (r) {
          setDocKey(d.key); setMissQuote(null);
          setQuoteRange({ docId: `${it.itemId}/${d.key}`, ...r });
          setQuoteNonce(x => x + 1);
          return;
        }
      }
      setQuoteRange(null); setMissQuote(focus.quote);
    })();
    return () => { cancelled = true; };
    // focus.nonce 가 바뀔 때만 (같은 근거를 다시 눌러도 다시 스크롤)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.nonce, items]);

  // ── 검색 ──
  const text = item && doc && doc.kind !== 'image' ? textOf(item, doc) : null;
  // 원본 서버에서 받지 못한 문서 — 예전 서버는 이유를 글 칸에 「(원문 받기 실패: …)」로 넣었다 (2026-10-03)
  const docErr: string | null = !doc ? null : doc.error ? doc.error : dlErr[docId] ? dlErr[docId]
    : (typeof doc.text === 'string' && /^\(원문 받기 실패: /.test(doc.text) ? doc.text.replace(/^\(원문 받기 실패: /, '').replace(/\)\s*$/, '') : null);
  const detailUrl = item?.docs.find(d => d.key === 'detail')?.fileUrl || null;
  const pdfFallback = !docErr && doc?.kind === 'pdf' && pdfFail.has(docId) && (typeof doc.text === 'string' || !!doc.converted);
  const isPdf = doc?.kind === 'pdf' && !pdfFallback;
  const hits = useMemo(() => {
    if (!text || search.trim().length < 1) return [] as { start: number; end: number }[];
    const rx = flexRx(search.trim(), 'gi');
    if (!rx) return [];
    const out: { start: number; end: number }[] = [];
    let m: RegExpExecArray | null;
    while ((m = rx.exec(text)) !== null && out.length < 500) { out.push({ start: m.index, end: m.index + m[0].length }); if (!m[0]) rx.lastIndex += 1; }
    return out;
  }, [text, search]);
  useEffect(() => { setHit(0); }, [search, docId]);
  useEffect(() => { setZoom(1); setPageInfo(null); }, [docId]);
  useEffect(() => { setZoom(1); }, [fitKey]);

  // PDF 형광 · 스크롤 목표 (검색 결과가 있으면 지금 결과, 없으면 인용)
  const pdfMarks = useMemo((): PdfMark[] => {
    if (!isPdf) return [];
    const ms: PdfMark[] = hits.map((h, i) => ({ ...h, k: i === hit ? 'ha' : 'h' }));
    if (quoteRange && quoteRange.docId === docId) ms.push({ start: quoteRange.start, end: quoteRange.end, k: 'q' });
    return ms;
  }, [isPdf, hits, hit, quoteRange, docId]);
  const pdfScroll = useMemo(() => {
    if (!isPdf) return null;
    if (hits.length) return { start: hits[hit]?.start ?? 0, nonce: hit + 1 + search.length * 1000 };
    if (quoteRange && quoteRange.docId === docId) return { start: quoteRange.start, nonce: -quoteNonce };
    return null;
  }, [isPdf, hits, hit, search, quoteRange, docId, quoteNonce]);

  const segs = useMemo(() => {
    if (!text || isPdf) return [];
    const marks: { start: number; end: number; k: 'q' | 'h' | 'ha' }[] = hits.map((h, i) => ({ ...h, k: i === hit ? 'ha' as const : 'h' as const }));
    if (quoteRange && quoteRange.docId === docId) marks.push({ start: quoteRange.start, end: quoteRange.end, k: 'q' });
    marks.sort((a, b) => a.start - b.start);
    const out: { t: string; k?: 'q' | 'h' | 'ha' }[] = [];
    let pos = 0;
    for (const m of marks) {
      if (m.start < pos) continue; // 겹치면 앞 것만
      if (m.start > pos) out.push({ t: text.slice(pos, m.start) });
      out.push({ t: text.slice(m.start, m.end), k: m.k });
      pos = m.end;
    }
    if (pos < text.length) out.push({ t: text.slice(pos) });
    return out;
  }, [text, hits, hit, quoteRange, docId, isPdf]);

  // 형광으로 스크롤 — 인용(q) 이 새로 생기면 그것, 검색이면 현재 검색 결과
  const scrollKey = `${quoteRange && quoteRange.docId === docId ? `q${quoteRange.start}` : ''}|${hits.length ? `h${hit}` : ''}|${docId}`;
  useLayoutEffect(() => {
    const el = markRef.current;
    if (el) scrollToCenter(el, true);
  }, [scrollKey]);

  // ── 글 선택 → 채우기 버튼 ──
  const onMouseUp = () => {
    setTimeout(() => {
      const sel = window.getSelection();
      const t = sel ? sel.toString().replace(/\s+/g, ' ').trim() : '';
      if (!sel || !t || t.length > 300 || !textRef.current || !sel.anchorNode || !textRef.current.contains(sel.anchorNode)) { setSelTip(null); return; }
      const rect = sel.getRangeAt(0).getBoundingClientRect();
      setSelTip({ x: rect.left + rect.width / 2, y: rect.top, text: t });
    }, 0);
  };
  useEffect(() => {
    if (!selTip) return;
    const hide = () => setSelTip(null);
    const box = textRef.current;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); hide(); window.getSelection()?.removeAllRanges(); } };
    box?.addEventListener('scroll', hide);
    window.addEventListener('keydown', esc, true);
    return () => { box?.removeEventListener('scroll', hide); window.removeEventListener('keydown', esc, true); };
  }, [selTip]);
  const pick = (f: PickField) => {
    if (!selTip) return;
    onPick(f, selTip.text);
    setSelTip(null);
    window.getSelection()?.removeAllRanges();
  };
  const dates = selTip ? parseDates(selTip.text) : [];

  // ── 그리기 ──
  const header = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: 35, boxSizing: 'border-box', padding: '0 8px 0 14px', borderBottom: `1px solid ${C.border}`, background: C.bgAlt, flexShrink: 0 }}>
      <span style={{ fontSize: T.sm, fontWeight: 700, color: C.text, flexShrink: 0 }}>원문</span>
      {items && items.length > 1 ? (
        <select value={itemIdx} onChange={e => { setItemIdx(Number(e.target.value)); setDocKey(''); setQuoteRange(null); }}
          style={{ height: 26, maxWidth: 170, minWidth: 0, padding: '0 6px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.xs, color: C.sub, background: C.white }}>
          {items.map((it, i) => <option key={it.itemId} value={i}>{(it.registeredAt || '').slice(0, 7).replace('-', '.') || `회차 ${i + 1}`}{i === 0 ? ' (최신)' : ''}</option>)}
        </select>
      ) : item?.registeredAt ? <span style={{ fontSize: T.xs, color: C.muted }}>{item.registeredAt.slice(0, 7).replace('-', '.')} 공고</span> : null}
      <span style={{ flex: 1 }} />
      {doc?.fileUrl && <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer" style={miniBtn} title={doc.key === 'detail' ? '공고 페이지' : `원본 파일 — ${doc.fileName}`}>{doc.key === 'detail' ? '공고 페이지' : '원본'} ↗</a>}
      {isPdf && pageInfo && <span style={{ fontSize: T.xs, color: C.muted, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{pageInfo.cur}/{pageInfo.total}쪽</span>}
      {src && doc && doc.kind !== 'text' && !docErr && <a href={src.url} target="_blank" rel="noopener noreferrer" style={miniBtn} title="새 창으로 열기 (브라우저 PDF 뷰어)">새 창 ↗</a>}
      {onCollapse && <button onClick={onCollapse} style={{ ...miniBtn, width: 26, padding: 0, justifyContent: 'center' }} title="원문 접기" aria-label="원문 접기">«</button>}
    </div>
  );

  if (loading || error || !items) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
        {header}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, padding: 24, textAlign: 'center' }}>
          <div style={{ fontSize: T.md, fontWeight: 600, color: error ? C.red : C.sub }}>{error || '공고문을 불러오는 중…'}</div>
          {!error && <div style={{ fontSize: T.xs, color: C.muted }}>처음 여는 공고는 최대 30초 걸릴 수 있어요</div>}
        </div>
      </div>
    );
  }

  markRef.current = null; // 이번 그리기의 형광만 스크롤 대상으로
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, position: 'relative' }}>
      {header}
      {/* 파일 탭 */}
      <div role="tablist" style={{ display: 'flex', gap: 2, padding: '0 8px', borderBottom: `1px solid ${C.border}`, overflowX: 'auto', flexShrink: 0 }}>
        {docs.map(d => (
          <button key={d.key} role="tab" aria-selected={doc?.key === d.key} title={d.fileName} onClick={() => setDocKey(d.key)} style={tabBtn(doc?.key === d.key)}>{kindLabel(d, docs)}</button>
        ))}
        {!docs.length && <span style={{ fontSize: T.xs, color: C.muted, padding: '10px 6px' }}>원문 파일이 없습니다</span>}
      </div>

      {/* 검색 · 안내 */}
      {doc && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          {doc.kind !== 'image' ? (
            <>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="원문에서 찾기"
                onKeyDown={e => { if (e.key === 'Enter' && hits.length) { e.preventDefault(); setHit(h => (e.shiftKey ? (h - 1 + hits.length) % hits.length : (h + 1) % hits.length)); } if (e.key === 'Escape') setSearch(''); }}
                style={{ flex: 1, minWidth: 0, height: 28, padding: '0 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none', boxSizing: 'border-box' }} />
              <span style={{ fontSize: T.xs, color: search && !hits.length ? C.red : C.muted, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', minWidth: 36, textAlign: 'right' }}>
                {search ? (hits.length ? `${hit + 1}/${hits.length}` : '없음') : ''}
              </span>
              <button disabled={!hits.length} onClick={() => setHit(h => (h - 1 + hits.length) % hits.length)} style={{ ...miniBtn, width: 26, padding: 0, justifyContent: 'center', opacity: hits.length ? 1 : 0.4 }} aria-label="이전">↑</button>
              <button disabled={!hits.length} onClick={() => setHit(h => (h + 1) % hits.length)} style={{ ...miniBtn, width: 26, padding: 0, justifyContent: 'center', opacity: hits.length ? 1 : 0.4 }} aria-label="다음">↓</button>
              {isPdf && (
                <>
                  <span style={{ width: 1, height: 18, background: C.border, margin: '0 2px' }} />
                  <button onClick={() => setZoom(z => Math.max(0.5, Math.round((z - 0.25) * 100) / 100))} style={{ ...miniBtn, width: 26, padding: 0, justifyContent: 'center' }} aria-label="축소" title="축소">−</button>
                  <button onClick={() => setZoom(1)} title="폭 맞춤" style={{ ...miniBtn, minWidth: 44, justifyContent: 'center', fontVariantNumeric: 'tabular-nums' }}>{zoom === 1 ? '폭 맞춤' : `${Math.round(zoom * 100)}%`}</button>
                  <button onClick={() => setZoom(z => Math.min(3, Math.round((z + 0.25) * 100) / 100))} style={{ ...miniBtn, width: 26, padding: 0, justifyContent: 'center' }} aria-label="확대" title="확대">+</button>
                </>
              )}
            </>
          ) : (
            <span style={{ fontSize: T.xs, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {doc.fileName}
            </span>
          )}
        </div>
      )}

      {missQuote && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 12px', background: AMBER.bg, borderBottom: `1px solid ${AMBER.border}`, flexShrink: 0 }}>
          <div style={{ flex: 1, minWidth: 0, fontSize: T.xs, color: AMBER.text, lineHeight: 1.5 }}>
            원문에서 이 문장을 못 찾았어요 — 위 검색창에 일부만 넣어 찾아보세요<br />
            <span style={{ fontWeight: 600 }}>“{cleanQuote(missQuote)}”</span>
          </div>
          <button onClick={() => { void navigator.clipboard?.writeText(cleanQuote(missQuote)); }} style={miniBtn}>복사</button>
          <button onClick={() => setMissQuote(null)} style={{ ...miniBtn, width: 26, padding: 0, justifyContent: 'center' }} aria-label="닫기">✕</button>
        </div>
      )}

      {/* 본문 */}
      <div style={{ flex: 1, minHeight: 0, position: 'relative', background: C.white }}>
        {doc && doc.kind === 'pdf' && !pdfFallback && !docErr && doc.viewUrl && (
          <GovFeedPdfView key={docId} viewUrl={doc.viewUrl} zoom={zoom} marks={pdfMarks} scrollMark={pdfScroll} boxRef={textRef} onMouseUp={onMouseUp}
            loadingLabel={doc.converted ? `${(doc.fileName.match(/\.([a-z0-9]+)$/i) || [])[1]?.toUpperCase() || 'HWP'} 공고문을 PDF로 바꾸는 중… (처음 한 번 몇 초)` : undefined}
            onError={(m) => {
              // 원본을 못 받은 경우(빈 파일·웹 페이지·연결)는 「바꾸기 실패」가 아니다 — 직접 받기 안내로
              if (/원본 서버|받지 못했|빈 파일|빈 쪽|열 수 없습니다|zero bytes|is empty/i.test(m || '')) {
                setDlErr(x => ({ ...x, [docId]: /zero bytes|is empty/i.test(m) ? '원본 서버가 빈 파일(0바이트)을 줬습니다 — 기관 누리집에서 직접 받아 보세요' : m }));
                return;
              }
              setPdfFail(f => new Set(f).add(docId));
              // 바꾸기 실패 + 글 없음 → 서버가 뽑은 글을 받아 온다
              if (doc.converted && typeof doc.text !== 'string' && item && doc.viewUrl) {
                const id = docId;
                api.govFeedDocText(`${doc.viewUrl}?as=text`).then(t => { textCache.set(id, t); setTexts(x => ({ ...x, [id]: t })); }).catch(() => {});
              }
            }}
            onPageInfo={pageCb}
            onModel={m => { if (!item || !doc) return; const id = `${item.itemId}/${doc.key}`; textCache.set(id, m.text); setTexts(x => (x[id] === m.text ? x : { ...x, [id]: m.text })); }} />
        )}
        {doc && doc.kind === 'image' && src && src.key === docId && (
          <div style={{ position: 'absolute', inset: 0, overflow: 'auto', background: C.bg, padding: 12, boxSizing: 'border-box' }}>
            <img src={src.url} alt={doc.fileName} onLoad={() => setFrameLoading(false)} style={{ maxWidth: '100%', display: 'block', margin: '0 auto', boxShadow: '0 1px 4px rgba(0,0,0,0.15)' }} />
          </div>
        )}
        {doc && doc.kind === 'image' && frameLoading && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.white, fontSize: T.sm, pointerEvents: 'none' }}>공고문 여는 중…</div>
        )}
        {pdfFallback && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1, padding: '5px 12px', fontSize: T.xs, color: AMBER.text, background: AMBER.bg, borderBottom: `1px solid ${AMBER.border}` }}>{doc?.converted ? '공고문을 PDF로 바꾸지 못해 본문 글로 보여 줍니다 — 원본은 「새 창」에서 내려받기' : 'PDF를 그리지 못해 서버가 뽑은 글로 보여 줍니다 — 모양은 「새 창」에서'}</div>}
        {doc && docErr && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, boxSizing: 'border-box', background: C.bg }}>
            <div style={{ maxWidth: 420, width: '100%', padding: '20px 22px', borderRadius: 12, background: C.white, border: `1px solid ${C.border}`, boxShadow: '0 1px 3px rgba(17,24,39,0.06)' }}>
              <div style={{ fontSize: T.md, fontWeight: 700, color: C.text, marginBottom: 6 }}>{/열 수 없|빈 쪽/.test(docErr) ? '여기서 열 수 없는 공고문 형식입니다' : '원본 서버가 공고문을 내주지 않습니다'}</div>
              <div style={{ fontSize: T.sm, color: C.sub, lineHeight: 1.6, marginBottom: 14 }}>
                {/웹 페이지/.test(docErr) ? '이 기관 누리집은 로그인하거나 공고 페이지를 거쳐야 첨부를 내려 줍니다. 아래에서 직접 열어 확인해 주세요 — 검토·등록은 「상세 요약」과 AI 분석 결과로 할 수 있습니다.' : docErr}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {detailUrl && <a href={detailUrl} target="_blank" rel="noopener noreferrer" style={{ ...miniBtn, height: 32, padding: '0 14px', background: C.accent, color: C.white, borderColor: C.accent, fontWeight: 600 }}>공고 페이지에서 열기 ↗</a>}
                {doc.fileUrl && <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer" style={{ ...miniBtn, height: 32, padding: '0 14px' }}>첨부 직접 받기 ↗</a>}
              </div>
            </div>
          </div>
        )}
        {doc && !docErr && (doc.kind === 'text' || pdfFallback) && (
          <div ref={textRef} onMouseUp={onMouseUp}
            style={{ position: 'absolute', inset: 0, overflowY: 'auto', padding: `${pdfFallback ? 40 : 14}px 18px 40px`, boxSizing: 'border-box', fontSize: T.md, lineHeight: 1.75, color: C.text, whiteSpace: 'pre-wrap', wordBreak: 'keep-all', overflowWrap: 'anywhere' }}>
            {text == null ? <span style={{ color: C.muted }}>글을 불러오는 중…</span>
              : !text.trim() ? <span style={{ color: C.muted }}>글이 비어 있습니다{docs.some(d => d.kind === 'pdf') ? ' — PDF 탭을 보세요' : ''}</span>
                : segs.map((sg, i) => (sg.k
                  ? <mark key={i} ref={el => { if (el && (sg.k === 'ha' || (sg.k === 'q' && !hits.length))) markRef.current = el; }}
                    style={{
                      background: sg.k === 'q' ? '#FDE68A' : sg.k === 'ha' ? '#FDBA74' : '#FEF3C7', color: 'inherit', borderRadius: 2,
                      boxShadow: sg.k === 'q' ? '0 0 0 2px #FDE68A' : 'none', padding: '1px 0',
                    }}>{sg.t}</mark>
                  : <span key={i}>{sg.t}</span>))}
          </div>
        )}
      </div>

      {/* 글 선택 → 칸 채우기 */}
      {selTip && (
        <div data-feed-popover="1" onMouseDown={e => e.preventDefault()}
          style={{ position: 'fixed', left: Math.max(8, Math.min(window.innerWidth - 8, selTip.x)), top: Math.max(8, selTip.y - 40), transform: 'translateX(-50%)', zIndex: 1300,
            display: 'flex', gap: 2, padding: 3, borderRadius: 8, background: C.text, boxShadow: '0 6px 18px rgba(17,24,39,0.25)' }}>
          {([['name', '사업명으로'], ['report_name', '리포트명으로'], ['amount', '금액으로'], ['agency', '수행기관으로'],
            ...(dates.length >= 2 ? [['period', '신청기간으로']] : dates.length === 1 ? [['deadline', '마감일로'], ['apply_start', '시작일로']] : [])] as [PickField, string][])
            .map(([f, label]) => (
              <button key={f} onClick={() => pick(f)} style={{ height: 26, padding: '0 9px', border: 'none', borderRadius: 6, background: 'transparent', color: C.white, fontSize: T.xs, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>{label}</button>
            ))}
        </div>
      )}
    </div>
  );
}
